import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Tabs,
  Tab,
  Alert,
} from "@mui/material";
import { ResizableBox } from "react-resizable";
import "react-resizable/css/styles.css";

const XlsxCsvViewer = ({ filePath }) => {
  const [sheetsData, setSheetsData] = useState({});
  const [activeSheet, setActiveSheet] = useState("");
  const [colWidths, setColWidths] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAndParseFile = async () => {
    try {
      const res = await fetch(filePath);
      if (!res.ok) {
        throw new Error("Failed to fetch file");
      }
      const arrayBuffer = await res.arrayBuffer();
      console.log("File fetched successfully", arrayBuffer);
      const workbook = XLSX.read(arrayBuffer, {
        type: "array",
      });

      const parsedSheets = {};
      workbook.SheetNames.forEach((sheetName) => {
        const sheet = workbook.Sheets[sheetName];
        const matrixData = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          raw: false,
        });
        parsedSheets[sheetName] = matrixData;
      });

      setSheetsData(parsedSheets);
      setActiveSheet(workbook.SheetNames[0]);

      const firstSheet = parsedSheets[workbook.SheetNames[0]] || [];
      const maxColumns =
        firstSheet.length > 0
          ? Math.max(...firstSheet.map((row) => row.length))
          : 0;
      const widths = {};
      for (let i = 0; i < maxColumns; i++) {
        widths[`Column ${i + 1}`] = 160;
      }
      setColWidths(widths);
    } catch (err) {
      console.error("Error parsing file:", err);
      setError("Failed to load and parse the file. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!filePath) return;
    setLoading(true);
    setError(null);
    fetchAndParseFile();
  }, [filePath]);

  const handleResize =
    (header) =>
    (event, { size }) => {
      setColWidths((prev) => ({
        ...prev,
        [header]: size.width,
      }));
    };

  const handleTabChange = (event, newValue) => {
    setActiveSheet(newValue);

    const sheetData = sheetsData[newValue] || [];
    const maxColumns =
      sheetData.length > 0
        ? Math.max(...sheetData.map((row) => row.length))
        : 0;

    const widths = {};
    for (let i = 0; i < maxColumns; i++) {
      widths[`Column ${i + 1}`] = colWidths[`Column ${i + 1}`] || 160;
    }
    setColWidths(widths);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!Object.keys(sheetsData).length) {
    return <Typography align="center">No data found</Typography>;
  }

  const columnIndexToLetter = (index) => {
    let letters = "";
    while (index >= 0) {
      letters = String.fromCharCode((index % 26) + 65) + letters;
      index = Math.floor(index / 26) - 1;
    }
    return letters;
  };

  const currentData = sheetsData[activeSheet] || [];
  const maxColumns =
    currentData.length > 0
      ? Math.max(...currentData.map((row) => row.length))
      : 0;
  const headers = Array.from({ length: maxColumns }, (_, i) =>
    columnIndexToLetter(i),
  );

  const formatCell = (value) => {
    if (value === null || value === undefined) return "-";
    return value;
  };

  return (
    <Paper
      elevation={3}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 125px)",
        p: 1,
      }}
    >
      <Typography variant="h6" mb={1}>
        File Preview
      </Typography>
      <Tabs
        value={activeSheet}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        TabIndicatorProps={{ style: { display: "none" } }}
      >
        {Object.keys(sheetsData).map((sheetName) => (
          <Tab
            key={sheetName}
            label={sheetName}
            value={sheetName}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              borderRadius: 2,
              mx: 1,
              backgroundColor:
                activeSheet === sheetName ? "lightblue" : "#e0e0e0",
              color: activeSheet === sheetName ? "#fff" : "#000",
              "&:hover": {
                backgroundColor:
                  activeSheet === sheetName ? "#6bb6ff" : "#d5d5d5",
              },
              minHeight: "36px",
              paddingX: 1,
              paddingY: 0.5,
            }}
          />
        ))}
      </Tabs>

      {/* Horizontal Scroll Container */}
      <Box
        sx={{
          flex: 1,
          mt: 1,
          overflowX: "auto",
        }}
      >
        {/* Vertical Scroll Container */}
        <Box
          sx={{
            minWidth: "100%",
            maxHeight: "100%",
            overflowY: "auto",
            borderTop: "1px solid #ddd",
            borderBottom: "1px solid #ddd",
          }}
        >
          <Table
            size="small"
            stickyHeader
            sx={{
              borderCollapse: "collapse",
              width: "max-content",
              minWidth: "100%",
            }}
          >
            <TableHead>
              <TableRow>
                {headers.map((header) => (
                  <TableCell
                    key={header}
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#e0e0e0",
                      border: "1px solid #ccc",
                      padding: 0,
                    }}
                  >
                    <ResizableBox
                      width={colWidths[header] || 160}
                      axis="x"
                      resizeHandles={["e"]}
                      onResize={handleResize(header)}
                      minConstraints={[80, 40]}
                      maxConstraints={[600, 300]}
                    >
                      <Box
                        sx={{
                          padding: "4px 8px",
                          fontWeight: "bold",
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          wordBreak: "break-word",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                          minHeight: "48px",
                          textAlign: "center",
                          width: "100% !important",
                        }}
                      >
                        {header}
                      </Box>
                    </ResizableBox>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {currentData.map((row, i) => (
                <TableRow
                  key={i}
                  sx={{
                    backgroundColor: i % 2 === 0 ? "#fff" : "#f9f9f9",
                  }}
                >
                  {headers.map((_, j) => (
                    <TableCell
                      key={j}
                      sx={{
                        border: "1px solid #eee",
                        padding: "4px 8px",
                        width: colWidths[`Column ${j + 1}`],
                        maxWidth: colWidths[`Column ${j + 1}`],
                        wordWrap: "break-word",
                        whiteSpace: "normal",
                      }}
                    >
                      {formatCell(row[j])}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Box>
    </Paper>
  );
};

export default XlsxCsvViewer;
